/* eslint-disable react/prop-types */
import React, { useRef, useState, useId } from 'react'
import PropTypes from 'prop-types'

import { useDrag, useDrop } from 'react-dnd'

import CheckboxInput from '../CheckboxInput'
import DropdownButton from '../DropdownButton'

const ColumnsDropdown = ({ schema, setSchema, disabled, stickyColumn, setSticky }) => {
  const [updatedSchema, setUpdatedSchema] = useState(schema)
  const uniqueID = useId()

  const reorderTempSchema = (itemIndex, dropIndex) => {
    // create a copy of the array
    const newSchema = [...updatedSchema]

    // swap items
    const itemTemp = newSchema[itemIndex]
    newSchema[itemIndex] = newSchema[dropIndex]
    newSchema[dropIndex] = itemTemp

    setUpdatedSchema(newSchema)
  }

  const ColumnItem = ({ columnItem, columnIndex }) => {
    const [{ isDragging }, drag] = useDrag(() => ({
      type: 'column',
      item: { index: columnIndex },
      collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
      end: (item, monitor) => {
        if (!monitor.getDropResult()) {
          setUpdatedSchema(schema)
        }
      }
    }), [updatedSchema])

    const [{ isOver }, drop] = useDrop(
      () => ({
        accept: 'column',
        drop: () => {
          setSchema(updatedSchema.map((col) => ({ ...col })))
        },
        collect: (monitor) => ({ isOver: !!monitor.isOver() }),
        hover(item) {
          const dragIndex = item.index
          const hoverIndex = columnIndex

          // Don't replace items with themselves
          if (dragIndex === hoverIndex) {
            return
          }

          // Time to actually perform the action
          reorderTempSchema(dragIndex, hoverIndex)

          // Mutate the item
          item.index = hoverIndex
        }
      }),
      [updatedSchema]
    )

    // Join the 2 refs together into one (both draggable and can be dropped on)
    const ref = useRef(null)
    const dragDropRef = drag(drop(ref))

    // Make the item being being dragged and the new position transparent, so it's easier to see where we drop them
    const opacity = isOver ? 0.5 : 1

    return (
      <div
        ref={dragDropRef}
        className='py-1'
        style={{
          minWidth: '200px',
          opacity,
          transition: 'opacity 0.25s ease' /* Transition the opacity property with ease timing function */
        }}
      >
        <div className='d-flex justify-content-between align-items-center'>
          <CheckboxInput
            name={`${columnItem.name}_column_${uniqueID}`}
            label={columnItem.display}
            values={{ [`${columnItem.name}_column_${uniqueID}`]: columnItem.isColumnVisible !== false }}
            onChange={() => {
              if (stickyColumn && columnItem.name === stickyColumn.name) {
                setSticky(null)
              }
              const newSchema = schema.map((col) => {
                if (col.name !== columnItem.name) return col
                return {
                  ...col,
                  isColumnVisible: col.isColumnVisible === false
                }
              })
              setSchema(newSchema)
              setUpdatedSchema(newSchema)
            }}
            errors={{}}
            touched={{}}
            noError
          />
          <span className='fa-solid fa-bars drag-drop-icon rounded pointer p-1 ms-3' />
        </div>
      </div>
    )
  }

  const options = updatedSchema.map((d, i) => {
    if (!d.name || d.header || d.isColumn === false || d.isOptionColumn) return false

    return (
      <ColumnItem
        key={`${d.name}-${i}`}
        columnItem={d}
        columnIndex={i}
      />
    )
  })

  return (
    <DropdownButton
      className='table-button-muted'
      ghost
      disabled={disabled}
      options={[{ text: true, content: options }]}
    >
      <span className='fa-solid fa-table-columns' /> Columns
    </DropdownButton>
  )
}

ColumnsDropdown.propTypes = {
  schema: PropTypes.array,
  setSchema: PropTypes.func,
  disabled: PropTypes.bool,
  stickyColumn: PropTypes.object,
  setSticky: PropTypes.func
}

export default ColumnsDropdown
